import { useLayoutEffect, useRef, useState, type MutableRefObject, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';

const GAP = 6;
const EDGE = 8;

export default function MenuPanel({ anchor, panelRef, align = 'end', id, className, onClick, children }: {
  anchor: RefObject<HTMLElement | null>;
  panelRef: MutableRefObject<HTMLDivElement | null>;
  align?: 'start' | 'end';
  id: string;
  className: string;
  onClick?: () => void;
  children: ReactNode;
}) {
  const [at, setAt] = useState<{ top: number; left: number; maxHeight: number } | null>(null);
  const live = useRef(align);
  live.current = align;

  useLayoutEffect(() => {
    const place = () => {
      const a = anchor.current?.getBoundingClientRect();
      const p = panelRef.current;
      if (!a || !p) return;
      const w = Math.min(p.offsetWidth, innerWidth - 2 * EDGE);
      const h = p.scrollHeight;
      const below = innerHeight - a.bottom - GAP - EDGE;
      const above = a.top - GAP - EDGE;
      const up = h > below && above > below;
      const room = up ? above : below;
      const left = live.current === 'start' ? a.left : a.right - w;
      setAt({
        left: Math.max(EDGE, Math.min(left, innerWidth - w - EDGE)),
        top: up ? Math.max(EDGE, a.top - GAP - Math.min(h, room)) : a.bottom + GAP,
        maxHeight: Math.max(120, room),
      });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [anchor, panelRef]);

  return createPortal(
    <div
      ref={panelRef}
      id={id}
      role="menu"
      className={`${className} is-floating`}
      style={at
        ? { top: at.top, left: at.left, maxHeight: at.maxHeight }
        : { top: 0, left: 0, visibility: 'hidden' }}
      onClick={onClick}
    >
      {children}
    </div>,
    document.body,
  );
}
